import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import ReactGA from "react-ga4";

// Window型の拡張
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

// GAイベントの型定義
export interface GAEvent {
  action: string;
  category: string;
  label: string;
  value?: number;
  [key: string]: any;
}

interface AnalyticsContextType {
  gaId: string | null;
  setGaId: (id: string) => void;
  userId: string | null;
  setUserId: (id: string) => void;
  episodeId: string | null;
  setEpisodeId: (id: string) => void;
  sendGAEvent: (event: GAEvent) => void;
  sendLiveCommerceEvent: {
    playStart: (episodeId?: string, userId?: string) => void;
    heart: (episodeId?: string, userId?: string, count?: number) => void;
    chat: (
      sender_type: "viewer" | "streamer",
      episodeId?: string,
      userId?: string
    ) => void;
    viewProduct: (
      productId: string,
      episodeId?: string,
      userId?: string
    ) => void;
    purchase: (
      transactionId: string,
      revenue: number,
      customUserId?: string,
      items?: Array<{
        id: string;
        name: string;
        price: number;
        quantity: number;
      }>,
      customEpisodeId?: string,
      currency?: string,
      referrer?: string
    ) => void;
  };
  sendBannerEvent: (
    action: string,
    episodeId: string,
    userId: string,
    bannerId: string,
    creativeId: string
  ) => void;
  pageview: (url: string) => void;
  getClientId: () => string | null;
  getClientIdAsync: () => Promise<string | null>;
  generateUserIdFromClientId: () => string | null;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined
);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const [gaId, setGaId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [episodeId, setEpisodeId] = useState<string | null>(null);

  // GAのClient IDを取得する関数（同期版）
  const getClientId = (): string | null => {
    if (!gaId) {
      console.warn("Google Analytics ID is not set. Cannot get client ID.");
      return null;
    }

    try {
      // 代替方法：GAのCookieから直接取得（同期処理）
      if (typeof document !== "undefined") {
        const gaCookie = document.cookie
          .split("; ")
          .find((row) => row.startsWith("_ga="));

        if (gaCookie) {
          const gaValue = gaCookie.split("=")[1];
          // _gaクッキーの形式: GA1.2.clientId
          const parts = gaValue.split(".");
          if (parts.length >= 3) {
            return `${parts[2]}.${parts[3]}`;
          }
        }
      }
    } catch (error) {
      console.warn("Failed to get GA client ID:", error);
    }

    return null;
  };

  // GAのClient IDを取得する関数（非同期版）
  const getClientIdAsync = (): Promise<string | null> => {
    if (!gaId) {
      console.warn("Google Analytics ID is not set. Cannot get client ID.");
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      try {
        // GAのgtagオブジェクトからClient IDを取得
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("get", gaId, "client_id", (clientId: string) => {
            resolve(clientId || null);
          });
          return;
        }

        // フォールバック：同期版を使用
        resolve(getClientId());
      } catch (error) {
        console.warn("Failed to get GA client ID:", error);
        resolve(null);
      }
    });
  };

  // Client IDからUser IDを生成する関数
  const generateUserIdFromClientId = (): string | null => {
    const clientId = getClientId();
    if (!clientId || typeof clientId !== "string") {
      return null;
    }

    // Client IDをベースにハッシュ化されたUser IDを生成
    // 注意：これは一意性を保つための簡易的な方法です
    const hash = clientId.split(".").join("_");
    return `user_${hash}`;
  };

  // userIdが設定されていない場合の自動設定
  useEffect(() => {
    if (gaId && !userId) {
      // まず同期版でClient IDベースのUser IDを生成を試みる
      const generatedUserId = generateUserIdFromClientId();
      if (generatedUserId) {
        setUserId(generatedUserId);
      } else {
        // 同期版で取得できない場合は非同期版を試す
        getClientIdAsync()
          .then((asyncClientId) => {
            if (asyncClientId && typeof asyncClientId === "string") {
              const hash = asyncClientId.split(".").join("_");
              const asyncUserId = `user_${hash}`;
              setUserId(asyncUserId);
            }
          })
          .catch((error) => {
            console.warn(
              "Failed to generate user ID from async client ID:",
              error
            );
          });
      }
    }
  }, [gaId, userId]);

  useEffect(() => {
    if (gaId) {
      // GAの初期化
      ReactGA.initialize(gaId);

      // 現在のページのパスを取得してページビューを送信
      const currentPath = window.location.pathname + window.location.search;
      pageview(currentPath);
    }
  }, [gaId]);

  // ページビューの送信
  const pageview = (url: string) => {
    if (gaId) {
      ReactGA.send({ hitType: "pageview", page: url });
    } else {
      console.warn("Google Analytics ID is not set. Pageview not sent:", url);
    }
  };

  // イベントの送信
  const sendGAEvent = (event: GAEvent) => {
    if (!gaId) {
      console.warn("Google Analytics ID is not set. Event not sent:", event);
      return;
    }

    const {
      action,
      category,
      label,
      value,
      episode_id,
      user_id,
      ...restParams
    } = event;

    // 重複計測を防ぐためのevent_idを生成
    const eventId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // ユーザーIDが設定されていて、イベントに含まれていなければ追加
    const eventParams = {
      ...restParams,
    };

    const prefixedParams: Record<string, any> = {};
    Object.entries(eventParams).forEach(([key, value]) => {
      const newKey = key.startsWith("fv_") ? key : `fv_${key}`;
      prefixedParams[newKey] = value;
    });

    // 固定パラメータも追加
    if (user_id) {
      prefixedParams.fv_user_id = user_id;
    } else if (userId) {
      prefixedParams.fv_user_id = userId;
    }
    if (episode_id) {
      prefixedParams.fv_episode_id = episode_id;
    } else if (episodeId) {
      prefixedParams.fv_episode_id = episodeId;
    }
    if (action) prefixedParams.fv_action = action;
    if (category) prefixedParams.fv_category = category;
    if (label) prefixedParams.fv_label = label;
    if (value !== undefined) prefixedParams.fv_value = value;

    // event_idを追加して重複計測を防ぐ
    prefixedParams.event_id = eventId;

    ReactGA.event(action, prefixedParams);
  };

  // ライブコマース特有のイベント送信メソッド
  const sendLiveCommerceEvent = {
    // 再生送信イベント
    playStart: (episodeIdParam?: string, userIdParam?: string) => {
      if (!gaId) {
        console.warn(
          "Google Analytics ID is not set. PlayStart event not sent."
        );
        return;
      }

      const eventUserId = userIdParam || userId;
      if (!eventUserId) {
        console.warn("User ID is not set. PlayStart event may be incomplete.");
      }

      const eventEpisodeId = episodeIdParam || episodeId;
      if (!eventEpisodeId) {
        console.warn(
          "Episode ID is not set. PlayStart event may be incomplete."
        );
      }

      sendGAEvent({
        action: "play_start",
        category: "foove_plus",
        label: eventEpisodeId || "",
        user_id: eventUserId,
        episode_id: eventEpisodeId,
      });
    },

    // ハート送信イベント
    heart: (
      episodeIdParam?: string,
      userIdParam?: string,
      count: number = 1
    ) => {
      if (!gaId) {
        console.warn("Google Analytics ID is not set. Heart event not sent.");
        return;
      }

      const eventUserId = userIdParam || userId;
      if (!eventUserId) {
        console.warn("User ID is not set. Heart event may be incomplete.");
      }

      const eventEpisodeId = episodeIdParam || episodeId;
      if (!eventEpisodeId) {
        console.warn("Episode ID is not set. Heart event may be incomplete.");
      }

      console.log("heart", {
        action: "send_heart",
        category: "foove_plus",
        label: eventEpisodeId || "",
        value: count,
        user_id: eventUserId,
        episode_id: eventEpisodeId,
      });

      sendGAEvent({
        action: "send_heart",
        category: "foove_plus",
        label: eventEpisodeId || "",
        value: count,
        user_id: eventUserId,
        episode_id: eventEpisodeId,
      });
    },

    // チャット送信イベント
    chat: (
      sender_type: "viewer" | "streamer",
      episodeIdParam?: string,
      userIdParam?: string
    ) => {
      if (!gaId) {
        console.warn("Google Analytics ID is not set. Chat event not sent.");
        return;
      }

      const eventUserId = userIdParam || userId;
      if (!eventUserId) {
        console.warn("User ID is not set. Chat event may be incomplete.");
      }

      const eventEpisodeId = episodeIdParam || episodeId;
      if (!eventEpisodeId) {
        console.warn("Episode ID is not set. Chat event may be incomplete.");
      }

      sendGAEvent({
        action: "send_chat",
        category: "foove_plus",
        label: eventEpisodeId || "",
        user_id: eventUserId,
        episode_id: eventEpisodeId,
        sender_type: sender_type,
      });
    },

    // 商品閲覧イベント
    viewProduct: (
      productId: string,
      episodeIdParam?: string,
      userIdParam?: string
    ) => {
      if (!gaId) {
        console.warn(
          "Google Analytics ID is not set. ViewProduct event not sent."
        );
        return;
      }

      const eventUserId = userIdParam || userId;
      if (!eventUserId) {
        console.warn(
          "User ID is not set. ViewProduct event may be incomplete."
        );
      }

      const eventEpisodeId = episodeIdParam || episodeId;
      if (!eventEpisodeId) {
        console.warn(
          "Episode ID is not set. ViewProduct event may be incomplete."
        );
      }

      sendGAEvent({
        action: "view_product",
        category: "foove_plus",
        label: productId,
        user_id: eventUserId,
        episode_id: eventEpisodeId,
        product_id: productId,
      });
    },

    // 購入イベント
    purchase: (
      transactionId: string,
      revenue: number,
      customUserId?: string,
      items?: Array<{
        id: string;
        name: string;
        price: number;
        quantity: number;
      }>,
      customEpisodeId?: string,
      currency: string = "JPY",
      referrer: string = ""
    ) => {
      if (!gaId) {
        console.warn(
          "Google Analytics ID is not set. Purchase event not sent."
        );
        return;
      }

      const eventUserId = customUserId || userId;
      const eventEpisodeId = customEpisodeId || episodeId;

      // アイテムのフォーマット変換
      const formattedItems = items
        ? items.map((item) => ({
            item_id: item.id,
            item_name: item.name,
            price: item.price,
            quantity: item.quantity,
          }))
        : [];

      // エピソードIDが指定されていて、アイテムに含まれていなければ追加
      if (
        eventEpisodeId &&
        !formattedItems.find((item) => item.item_id === eventEpisodeId)
      ) {
        formattedItems.push({
          item_id: eventEpisodeId,
          item_name: `Episode ${eventEpisodeId}`,
          price: revenue,
          quantity: 1,
        });
      }

      // GA4イベントパラメータ
      const eventParams: Record<string, any> = {
        transaction_id: transactionId,
        value: revenue,
        currency: currency,
        items: formattedItems,
      };

      // エピソードIDが指定されていれば追加
      if (eventEpisodeId) {
        eventParams.episode_id = eventEpisodeId;
      }

      sendGAEvent({
        action: "purchase",
        category: "foove_plus",
        label: transactionId,
        value: revenue,
        user_id: eventUserId,
        ...eventParams,
      });
    },
  };

  const sendBannerEvent = (
    action: string,
    episodeId: string,
    userId: string,
    bannerId: string,
    creativeId: string
  ) => {
    sendGAEvent({
      action,
      category: "foove_plus_banner",
      label: bannerId,
      episode_id: episodeId,
      user_id: userId,
      creative_id: creativeId,
    });
  };

  return (
    <AnalyticsContext.Provider
      value={{
        gaId,
        setGaId,
        userId,
        setUserId,
        episodeId,
        setEpisodeId,
        sendGAEvent,
        sendLiveCommerceEvent,
        sendBannerEvent,
        pageview,
        getClientId,
        getClientIdAsync,
        generateUserIdFromClientId,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
}
